import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import React__default, { useState, useEffect } from "react";
import { renderMessageContentWithCarousels } from "../utils/messageRenderer.js";
import { ProductGroupCarousel } from "../ProductGroupCarousel.js";
import { FeaturedProductWidget } from "./FeaturedProductWidget.js";
import { SurveyCarousel } from "./SurveyCarousel.js";
import { ProductSearchQueriesSection } from "./ProductSearchQueriesSection.js";
import { getRetailerFromId } from "../../utils/stringUtils.js";
import { getDefaultProductImage, getFallbackProductImage } from "../../utils/assetUtils.js";
const isCallState = (state) => state === "call" || state === "partial-call" || state === "input-streaming" || state === "input-available";
const isOutputState = (state) => state === "result" || state === "output-available" || state === "output" || state === "complete";
function normalizeDynamoValue(value) {
  if (value === null || value === void 0) return value;
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((item) => normalizeDynamoValue(item));
  if ("value" in value && Object.keys(value).length === 1) {
    return normalizeDynamoValue(value.value);
  }
  const normalized = {};
  for (const key of Object.keys(value)) {
    normalized[key] = normalizeDynamoValue(value[key]);
  }
  return normalized;
}
function ConciergePartRenderer({
  parts,
  productMetadataMap,
  onProductClick,
  isStreaming,
  pdpStacklineSku
}) {
  if (!parts || parts.length === 0) return null;
  const normalizedParts = normalizeDynamoValue(parts);
  const hasDataSections = normalizedParts.some((p) => p.type === "data-section");
  const hasDataFeaturedProduct = normalizedParts.some((p) => p.type === "data-featured-product");
  const hasDataSurveySections = normalizedParts.some((p) => p.type === "data-survey-section");
  const hasSearchQueryResults = normalizedParts.some((p) => p.type === "data-search-query-result");
  const searchQueryResults = normalizedParts.filter((p) => p.type === "data-search-query-result").map((p) => p.data);
  return /* @__PURE__ */ jsx(Fragment, { children: normalizedParts.map((part, index) => /* @__PURE__ */ jsx(
    ConciergePartItem,
    {
      part,
      productMetadataMap,
      onProductClick,
      hasDataSections,
      hasDataFeaturedProduct,
      hasDataSurveySections,
      hasSearchQueryResults,
      searchQueryResults,
      isStreaming,
      pdpStacklineSku
    },
    `${part.type}-${index}`
  )) });
}
function ConciergePartItem({
  part,
  productMetadataMap,
  onProductClick,
  hasDataSections,
  hasDataFeaturedProduct,
  hasDataSurveySections,
  hasSearchQueryResults,
  searchQueryResults,
  isStreaming,
  pdpStacklineSku
}) {
  switch (part.type) {
    case "text":
      return /* @__PURE__ */ jsx(
        TextPart,
        {
          text: part.text || "",
          productMetadataMap,
          onProductClick
        }
      );
    case "tool-analyze_query":
      return /* @__PURE__ */ jsx(AnalyzeQueryPart, { part });
    case "tool-search_products":
      return /* @__PURE__ */ jsx(
        SearchProductsPart,
        {
          part,
          onProductClick,
          searchQueryResults: searchQueryResults || [],
          hasSearchQueryResults,
          isStreaming
        }
      );
    case "tool-filter_results":
      return /* @__PURE__ */ jsx(FilterResultsPart, { part });
    case "tool-organize_results":
      return /* @__PURE__ */ jsx(
        OrganizeResultsPart,
        {
          part,
          productMetadataMap,
          suppressOutput: hasDataSections
        }
      );
    case "data-section":
      return /* @__PURE__ */ jsx(DataSectionPart, { part, productMetadataMap });
    case "tool-get_daily_deals":
      return /* @__PURE__ */ jsx(DailyDealsPart, { part, onProductClick });
    case "data-survey-section":
      return /* @__PURE__ */ jsx(DataSurveySectionPart, { part });
    case "tool-get_surveys":
      return /* @__PURE__ */ jsx(SurveysPart, { part, suppressOutput: hasDataSurveySections });
    case "tool-show_store_locator":
      return /* @__PURE__ */ jsx(StoreLocatorPart, { part });
    case "tool-get_product_details":
      return /* @__PURE__ */ jsx(ProductDetailsPart, { part, onProductClick, pdpStacklineSku });
    case "tool-compare_products":
      return /* @__PURE__ */ jsx(CompareProductsPart, { part });
    case "tool-show_featured_product":
      return /* @__PURE__ */ jsx(FeaturedProductPart, { part, productMetadataMap, suppressOutput: hasDataFeaturedProduct, pdpStacklineSku });
    case "data-featured-product":
      return /* @__PURE__ */ jsx(DataFeaturedProductPart, { part, productMetadataMap, pdpStacklineSku });
    case "data-search-query-result":
    case "tool-route_to_support":
    case "tool-search_faq":
    case "tool-escalate_to_human_support":
    case "tool-ask_user_for_information":
    case "tool-query_knowledge_base":
      return null;
    default:
      return null;
  }
}
function CollapsibleProductSummary({
  product,
  defaultOpen,
  children
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const price = product.retailPrice != null ? `$${Number(product.retailPrice).toFixed(2)}` : "";
  const rating = product.reviewsRating != null ? `★ ${Number(product.reviewsRating).toFixed(1)}` : "";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        overflow: "hidden",
        margin: "8px 0"
      },
      children: [
        /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            onClick: () => setIsOpen((o) => !o),
            style: {
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "8px 12px",
              background: "#fafafa",
              border: "none",
              cursor: "pointer",
              fontSize: 13,
              color: "#333",
              textAlign: "left"
            },
            children: [
              /* @__PURE__ */ jsxs("span", { style: { flex: 1, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: [
                product.brandName ? `${product.brandName} · ` : "",
                product.title
              ] }),
              price && /* @__PURE__ */ jsx("span", { style: { color: "#555", whiteSpace: "nowrap" }, children: price }),
              rating && /* @__PURE__ */ jsx("span", { style: { color: "#555", whiteSpace: "nowrap" }, children: rating }),
              /* @__PURE__ */ jsx("span", { style: { fontSize: 10, color: "#888", marginLeft: 4 }, children: isOpen ? "▲" : "▼" })
            ]
          }
        ),
        isOpen && /* @__PURE__ */ jsx("div", { style: { padding: "0 4px 4px" }, children })
      ]
    }
  );
}
function TextPart({
  text,
  productMetadataMap,
  onProductClick
}) {
  if (!text.trim()) return null;
  const renderedContent = renderMessageContentWithCarousels(
    text,
    productMetadataMap || /* @__PURE__ */ new Map(),
    /* @__PURE__ */ new Map(),
    false,
    false
  );
  if (renderedContent.length > 0) {
    return /* @__PURE__ */ jsx(Fragment, { children: renderedContent });
  }
  return /* @__PURE__ */ jsx("div", { className: "concierge-text-part", children: text.split("\n\n").map((paragraph, i) => /* @__PURE__ */ jsx("p", { children: paragraph }, i)) });
}
function SearchProductsPart({
  part,
  onProductClick,
  searchQueryResults,
  hasSearchQueryResults,
  isStreaming
}) {
  var _a, _b, _c, _d;
  const searchAngles = (_a = part.input) == null ? void 0 : _a.searchAngles;
  const hasAngles = Array.isArray(searchAngles) && searchAngles.length > 0;
  if (hasAngles) {
    const previewSearchQueries = searchAngles.map(
      (a) => (a.product || "").trim()
    );
    const isSearchActive = isCallState(part.state);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        ProductSearchQueriesSection,
        {
          previewSearchQueries,
          previewQueryProducts: searchQueryResults,
          isStreaming: isSearchActive,
          hasContent: !isSearchActive
        }
      ),
      isOutputState(part.state) && ((_b = part.output) == null ? void 0 : _b.noRelevantProducts) && /* @__PURE__ */ jsx("div", { className: "concierge-no-results", children: "No relevant products found for this search." })
    ] });
  }
  if (isCallState(part.state)) {
    const query = ((_c = part.input) == null ? void 0 : _c.query) || "products";
    return /* @__PURE__ */ jsx("div", { className: "concierge-search-loading", children: /* @__PURE__ */ jsxs("div", { className: "concierge-search-loading-text", children: [
      "Searching for ",
      query,
      "..."
    ] }) });
  }
  if (isOutputState(part.state) && ((_d = part.output) == null ? void 0 : _d.noRelevantProducts)) {
    return /* @__PURE__ */ jsx("div", { className: "concierge-no-results", children: "No relevant products found for this search." });
  }
  return null;
}
function AnalyzeQueryPart({ part }) {
  if (isCallState(part.state)) {
    return /* @__PURE__ */ jsx("div", { className: "concierge-search-loading", children: /* @__PURE__ */ jsx("div", { className: "concierge-search-loading-text", children: "Analyzing your query..." }) });
  }
  if (isOutputState(part.state) && part.output) {
    const { intent, brands, features, searchAngles, numAngles } = part.output;
    return /* @__PURE__ */ jsx("div", { className: "concierge-search-results", children: /* @__PURE__ */ jsxs("div", { className: "concierge-search-summary", children: [
      intent && /* @__PURE__ */ jsxs("span", { children: [
        "Intent: ",
        intent
      ] }),
      (brands == null ? void 0 : brands.length) > 0 && /* @__PURE__ */ jsxs("span", { children: [
        " · Brands: ",
        brands.join(", ")
      ] }),
      (features == null ? void 0 : features.length) > 0 && /* @__PURE__ */ jsxs("span", { children: [
        " · Features: ",
        features.join(", ")
      ] }),
      numAngles > 0 && /* @__PURE__ */ jsxs("span", { children: [
        " · ",
        numAngles,
        " search angle",
        numAngles !== 1 ? "s" : "",
        " planned"
      ] })
    ] }) });
  }
  return null;
}
function FilterResultsPart({ part }) {
  if (isCallState(part.state)) {
    return /* @__PURE__ */ jsx("div", { className: "concierge-search-loading", children: /* @__PURE__ */ jsx("div", { className: "concierge-search-loading-text", children: "Filtering results..." }) });
  }
  if (isOutputState(part.state) && part.output) {
    const { originalCount, filteredCount, appliedFilters } = part.output;
    if (originalCount === 0) return null;
    return /* @__PURE__ */ jsx("div", { className: "concierge-search-results", children: /* @__PURE__ */ jsxs("div", { className: "concierge-search-summary", children: [
      "Filtered ",
      originalCount,
      " → ",
      filteredCount,
      " products",
      (appliedFilters == null ? void 0 : appliedFilters.length) > 0 && /* @__PURE__ */ jsxs("span", { children: [
        " (",
        appliedFilters.join(", "),
        ")"
      ] })
    ] }) });
  }
  return null;
}
function toolProductToSectionProduct(p) {
  const reward = p.totalRewardAmount ?? p.rewardAmount ?? 0;
  return {
    stacklineSku: p.sku,
    title: p.title || "",
    retailPrice: p.price ?? 0,
    retailerId: p.retailerId ?? 0,
    reviewsRating: p.rating ?? 0,
    reviewsAverageRating: p.rating ?? 0,
    reviewsCount: p.reviewCount ?? 0,
    hasRewards: !!p.hasRewards || reward > 0,
    totalRewardAmount: reward,
    affiliateRewardAmount: 0,
    hasActiveInstantReward: false,
    isAvailable: p.isAvailable ?? true,
    imageUrls: p.imageUrl ? [p.imageUrl] : [],
    brandName: p.brand || ""
  };
}
const SECTION_STAGGER_MS = 300;
const sectionFadeInStyle = `
@keyframes concierge-fade-in {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}`;
function OrganizeResultsPart({
  part,
  productMetadataMap,
  suppressOutput
}) {
  var _a;
  const sections = isOutputState(part.state) ? (_a = part.output) == null ? void 0 : _a.sections : void 0;
  const sectionCount = (sections == null ? void 0 : sections.length) ?? 0;
  const [visibleCount, setVisibleCount] = useState(0);
  useEffect(() => {
    if (!sectionCount) {
      setVisibleCount(0);
      return;
    }
    setVisibleCount(0);
    let count = 0;
    const timer = setInterval(() => {
      count += 1;
      setVisibleCount(count);
      if (count >= sectionCount) clearInterval(timer);
    }, SECTION_STAGGER_MS);
    return () => clearInterval(timer);
  }, [sectionCount]);
  if (isCallState(part.state)) {
    return /* @__PURE__ */ jsx("div", { className: "concierge-organizing", children: /* @__PURE__ */ jsx("div", { className: "concierge-organizing-text", children: "Organizing results..." }) });
  }
  if (suppressOutput) return null;
  if (isOutputState(part.state) && sections && sections.length > 0) {
    return /* @__PURE__ */ jsxs("div", { className: "concierge-product-sections", children: [
      /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: sectionFadeInStyle } }),
      sections.map((section, sIdx) => {
        if (sIdx >= visibleCount) return null;
        const sectionSkus = (section.products || []).map((p) => p.sku).filter(Boolean);
        const sectionProducts = sectionSkus.map((sku) => productMetadataMap == null ? void 0 : productMetadataMap.get(sku)).filter(Boolean);
        const products = sectionProducts.length > 0 ? sectionProducts : (section.products || []).map(toolProductToSectionProduct);
        if (products.length === 0) return null;
        return /* @__PURE__ */ jsxs(
          "div",
          {
            className: "concierge-section",
            style: {
              animation: "concierge-fade-in 0.35s ease-out both"
            },
            children: [
              /* @__PURE__ */ jsx("h3", { className: "concierge-section-title", children: section.title }),
              section.description && /* @__PURE__ */ jsx("p", { className: "concierge-section-description", children: section.description }),
              /* @__PURE__ */ jsx(ProductGroupCarousel, { products, allStacklineSkus: sectionSkus })
            ]
          },
          sIdx
        );
      })
    ] });
  }
  return null;
}
function DataSectionPart({
  part,
  productMetadataMap
}) {
  const section = part.data;
  if (!section || !section.products || section.products.length === 0) return null;
  const sectionSkus = section.products.map((p) => p.sku).filter(Boolean);
  const sectionProducts = sectionSkus.map((sku) => productMetadataMap == null ? void 0 : productMetadataMap.get(sku)).filter(Boolean);
  const products = sectionProducts.length > 0 ? sectionProducts : section.products.map(toolProductToSectionProduct);
  if (products.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "concierge-product-sections", children: [
    /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: sectionFadeInStyle } }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-section",
        style: { animation: "concierge-fade-in 0.35s ease-out both" },
        children: [
          /* @__PURE__ */ jsx("h3", { className: "concierge-section-title", children: section.title }),
          section.description && /* @__PURE__ */ jsx("p", { className: "concierge-section-description", children: section.description }),
          /* @__PURE__ */ jsx(ProductGroupCarousel, { products, allStacklineSkus: sectionSkus })
        ]
      }
    )
  ] });
}
function toolOutputToFeaturedProduct(p) {
  return {
    stacklineSku: p.stacklineSku || p.sku,
    title: p.title || "",
    retailPrice: p.retailPrice ?? p.price ?? 0,
    retailerId: p.retailerId ?? 0,
    reviewsRating: p.reviewsRating ?? p.rating ?? 0,
    reviewsAverageRating: p.reviewsAverageRating ?? p.reviewsRating ?? p.rating ?? 0,
    reviewsCount: p.reviewsCount ?? p.reviewCount ?? 0,
    hasRewards: !!p.hasRewards,
    totalRewardAmount: p.totalRewardAmount ?? 0,
    affiliateRewardAmount: 0,
    hasActiveInstantReward: false,
    isAvailable: p.isAvailable ?? true,
    imageUrls: p.imageUrls || (p.imageUrl ? [p.imageUrl] : []),
    brandName: p.brandName || p.brand || "",
    featureBullets: p.featureBullets || [],
    variants: p.variants || [],
    variationNameValues: p.variationNameValues || {},
    checkoutEligibility: p.checkoutEligibility,
    retailerDisplayName: p.retailerDisplayName || "",
    retailerProductPageUrl: p.retailerProductPageUrl || "",
    campaignRewards: p.campaignRewards || []
  };
}
function FeaturedProductPart({
  part,
  productMetadataMap,
  suppressOutput,
  pdpStacklineSku
}) {
  if (isCallState(part.state)) {
    return /* @__PURE__ */ jsx("div", { className: "concierge-loading", children: "Loading product details..." });
  }
  if (suppressOutput) return null;
  if (isOutputState(part.state) && part.output && !part.output.error) {
    const sku = part.output.sku || part.output.stacklineSku;
    const product = (productMetadataMap == null ? void 0 : productMetadataMap.get(sku)) || toolOutputToFeaturedProduct(part.output);
    const isPdpContext = !!(pdpStacklineSku && sku === pdpStacklineSku) || !!part.output.fromPdp;
    if (isPdpContext) {
      return /* @__PURE__ */ jsx(CollapsibleProductSummary, { product, defaultOpen: false, children: /* @__PURE__ */ jsx(FeaturedProductWidget, { product }) });
    }
    return /* @__PURE__ */ jsx(FeaturedProductWidget, { product });
  }
  return null;
}
function DataFeaturedProductPart({
  part,
  productMetadataMap,
  pdpStacklineSku
}) {
  const data = part.data;
  if (!data || !data.stacklineSku) return null;
  const product = (productMetadataMap == null ? void 0 : productMetadataMap.get(data.stacklineSku)) || toolOutputToFeaturedProduct(data);
  const isPdpContext = !!(pdpStacklineSku && data.stacklineSku === pdpStacklineSku);
  const content = /* @__PURE__ */ jsxs("div", { style: { animation: "concierge-fade-in 0.35s ease-out both" }, children: [
    /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: sectionFadeInStyle } }),
    /* @__PURE__ */ jsx(FeaturedProductWidget, { product })
  ] });
  if (isPdpContext) {
    return /* @__PURE__ */ jsx(CollapsibleProductSummary, { product, defaultOpen: false, children: content });
  }
  return content;
}
function DailyDealsPart({
  part,
  onProductClick
}) {
  var _a, _b;
  if (isCallState(part.state)) return /* @__PURE__ */ jsx("div", { className: "concierge-loading", children: "Loading daily deals..." });
  if (isOutputState(part.state) && ((_b = (_a = part.output) == null ? void 0 : _a.deals) == null ? void 0 : _b.length) > 0) {
    const deals = part.output.deals;
    const products = deals.map((d) => toolProductToSectionProduct(d));
    const allSkus = deals.map((d) => d.sku).filter(Boolean);
    return /* @__PURE__ */ jsxs("div", { className: "concierge-product-sections", children: [
      /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: sectionFadeInStyle } }),
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: "concierge-section",
          style: { animation: "concierge-fade-in 0.35s ease-out both" },
          children: [
            /* @__PURE__ */ jsx("h3", { className: "concierge-section-title", children: "Daily Deals" }),
            /* @__PURE__ */ jsx(ProductGroupCarousel, { products, allStacklineSkus: allSkus })
          ]
        }
      )
    ] });
  }
  return null;
}
function SurveysPart({ part, suppressOutput }) {
  var _a, _b;
  if (isCallState(part.state)) return /* @__PURE__ */ jsx("div", { className: "concierge-loading", children: "Loading surveys..." });
  if (suppressOutput) return null;
  if (isOutputState(part.state) && ((_b = (_a = part.output) == null ? void 0 : _a.surveys) == null ? void 0 : _b.length) > 0) {
    return /* @__PURE__ */ jsx(SurveyCarousel, { surveys: part.output.surveys });
  }
  return null;
}
function DataSurveySectionPart({ part }) {
  var _a;
  const section = part.data;
  if (!((_a = section == null ? void 0 : section.surveys) == null ? void 0 : _a.length)) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "concierge-survey-section",
      style: { animation: "concierge-fade-in 0.35s ease-out both" },
      children: [
        /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: sectionFadeInStyle } }),
        section.title && /* @__PURE__ */ jsx("h3", { className: "concierge-section-title", children: section.title }),
        /* @__PURE__ */ jsx(SurveyCarousel, { surveys: section.surveys })
      ]
    }
  );
}
function StoreLocatorPart({ part }) {
  if (isCallState(part.state)) return /* @__PURE__ */ jsx("div", { className: "concierge-loading", children: "Finding stores..." });
  if (isOutputState(part.state) && part.output) {
    return /* @__PURE__ */ jsx("div", { className: "concierge-store-locator", children: part.output.isOnlineOnly ? /* @__PURE__ */ jsx("div", { children: "This product is available online only." }) : part.output.hasStores ? /* @__PURE__ */ jsxs("div", { children: [
      "Store locator: ",
      part.output.stacklineSku
    ] }) : /* @__PURE__ */ jsx("div", { children: "No nearby stores found for this product." }) });
  }
  return null;
}
const GREY_PLACEHOLDER_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Crect width='80' height='80' fill='%23f5f5f5' rx='6'/%3E%3C/svg%3E";
function CompareProductImage({ sku, imageUrl, title }) {
  const fallbacks = React__default.useMemo(() => {
    const seen = /* @__PURE__ */ new Set();
    const urls = [];
    for (const url of [imageUrl, getDefaultProductImage(sku), getFallbackProductImage(sku)]) {
      if (url && !seen.has(url)) {
        seen.add(url);
        urls.push(url);
      }
    }
    urls.push(GREY_PLACEHOLDER_SVG);
    return urls;
  }, [sku, imageUrl]);
  const [idx, setIdx] = React__default.useState(0);
  return /* @__PURE__ */ jsx(
    "img",
    {
      src: fallbacks[idx],
      alt: title,
      className: "concierge-compare-image",
      onError: () => setIdx((i) => Math.min(i + 1, fallbacks.length - 1))
    }
  );
}
function CompareProductsPart({ part }) {
  var _a, _b;
  if (isCallState(part.state)) {
    return /* @__PURE__ */ jsx("div", { className: "concierge-loading", children: "Comparing products..." });
  }
  if (isOutputState(part.state) && ((_b = (_a = part.output) == null ? void 0 : _a.products) == null ? void 0 : _b.length) >= 2) {
    const products = part.output.products;
    const ROW_DEFS = [
      {
        label: "Price",
        render: (p) => p.price != null ? /* @__PURE__ */ jsxs("span", { className: "concierge-compare-price", children: [
          "$",
          Number(p.price).toFixed(2)
        ] }) : "—"
      },
      {
        label: "Rating",
        render: (p) => p.rating != null ? /* @__PURE__ */ jsxs("span", { children: [
          "★ ",
          Number(p.rating).toFixed(1),
          p.reviewCount != null && /* @__PURE__ */ jsxs("span", { className: "concierge-compare-muted", children: [
            " (",
            p.reviewCount.toLocaleString(),
            ")"
          ] })
        ] }) : "—"
      },
      {
        label: "Retailer",
        render: (p) => p.retailerId ? getRetailerFromId(p.retailerId) || "—" : "—"
      },
      {
        label: "Availability",
        render: (p) => p.isAvailable == null ? "—" : p.isAvailable ? /* @__PURE__ */ jsx("span", { className: "concierge-compare-available", children: "In stock" }) : /* @__PURE__ */ jsx("span", { className: "concierge-compare-unavailable", children: "Out of stock" })
      },
      {
        label: "Rewards",
        render: (p) => p.hasRewards && p.totalRewardAmount != null ? /* @__PURE__ */ jsxs("span", { className: "concierge-compare-reward", children: [
          "$",
          Number(p.totalRewardAmount).toFixed(2),
          " cashback"
        ] }) : /* @__PURE__ */ jsx("span", { className: "concierge-compare-muted", children: "None" })
      }
    ];
    return /* @__PURE__ */ jsx("div", { className: "concierge-compare-wrapper", children: /* @__PURE__ */ jsx("div", { className: "concierge-compare-scroll", children: /* @__PURE__ */ jsxs("table", { className: "concierge-compare-table", children: [
      /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
        /* @__PURE__ */ jsx("th", { className: "concierge-compare-attr-col" }),
        products.map((p, i) => /* @__PURE__ */ jsxs("th", { className: "concierge-compare-product-col", children: [
          /* @__PURE__ */ jsx(CompareProductImage, { sku: p.sku, imageUrl: p.imageUrl, title: p.title }),
          /* @__PURE__ */ jsx("div", { className: "concierge-compare-brand", children: p.brand }),
          /* @__PURE__ */ jsx("div", { className: "concierge-compare-title", children: p.title })
        ] }, p.sku || i))
      ] }) }),
      /* @__PURE__ */ jsxs("tbody", { children: [
        ROW_DEFS.map((row) => /* @__PURE__ */ jsxs("tr", { className: "concierge-compare-row", children: [
          /* @__PURE__ */ jsx("td", { className: "concierge-compare-label", children: row.label }),
          products.map((p, i) => /* @__PURE__ */ jsx("td", { className: "concierge-compare-cell", children: row.render(p) }, p.sku || i))
        ] }, row.label)),
        products.some((p) => {
          var _a2;
          return ((_a2 = p.featureBullets) == null ? void 0 : _a2.length) > 0;
        }) && /* @__PURE__ */ jsxs("tr", { className: "concierge-compare-row", children: [
          /* @__PURE__ */ jsx("td", { className: "concierge-compare-label", children: "Highlights" }),
          products.map((p, i) => {
            var _a2;
            return /* @__PURE__ */ jsx("td", { className: "concierge-compare-cell", children: ((_a2 = p.featureBullets) == null ? void 0 : _a2.length) > 0 ? /* @__PURE__ */ jsx("ul", { className: "concierge-compare-bullets", children: p.featureBullets.map((b, bi) => /* @__PURE__ */ jsx("li", { children: b }, bi)) }) : "—" }, p.sku || i);
          })
        ] })
      ] })
    ] }) }) });
  }
  return null;
}
function ProductDetailsPart({
  part,
  onProductClick,
  pdpStacklineSku
}) {
  var _a, _b;
  if (isCallState(part.state)) return /* @__PURE__ */ jsx("div", { className: "concierge-loading", children: "Loading product details..." });
  if (isOutputState(part.state) && ((_b = (_a = part.output) == null ? void 0 : _a.products) == null ? void 0 : _b.length) > 0) {
    const products = part.output.products;
    const isPdpContext = !!(pdpStacklineSku && products.every((p) => p.sku === pdpStacklineSku)) || !!part.output.fromPdp;
    if (isPdpContext && part.output.featuredProduct) {
      const product = toolOutputToFeaturedProduct(part.output.featuredProduct);
      return /* @__PURE__ */ jsx(CollapsibleProductSummary, { product, defaultOpen: false, children: /* @__PURE__ */ jsx(FeaturedProductWidget, { product }) });
    }
    const content = /* @__PURE__ */ jsx("div", { className: "concierge-product-details", children: products.map((p, i) => /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-product-detail-card",
        onClick: () => onProductClick == null ? void 0 : onProductClick(p.sku),
        role: "button",
        tabIndex: 0,
        children: [
          p.imageUrl && /* @__PURE__ */ jsx("img", { src: p.imageUrl, alt: p.title }),
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("div", { children: p.brand }),
            /* @__PURE__ */ jsx("div", { children: p.title }),
            /* @__PURE__ */ jsxs("div", { children: [
              "$",
              p.price != null ? Number(p.price).toFixed(2) : ""
            ] }),
            p.description && /* @__PURE__ */ jsx("div", { children: p.description })
          ] })
        ]
      },
      p.sku || i
    )) });
    if (isPdpContext) {
      const first = products[0];
      return /* @__PURE__ */ jsx(
        CollapsibleProductSummary,
        {
          product: toolProductToSectionProduct(first),
          defaultOpen: false,
          children: content
        }
      );
    }
    return content;
  }
  return null;
}
export {
  ConciergePartRenderer
};
//# sourceMappingURL=ConciergePartRenderer.js.map
